import { QueryKeys } from "@/constants/query-keys";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { EditDailyCaloriesModal } from "../../components/edit-daily-calories-modal";
import { EditMacrosModal } from "../../components/edit-macros-modal";
import { NutritionSummaryCard } from "../../components/nutrition-summary-card";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";

type ActiveModal = null | "calories" | "macros";

const OnboardingSummaryPage = () => {
  const { navigateToNextStep } = useStepRouteInfo();
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // The profile-write mutations (goal, activity, body stats, dob/sex) now
  // invalidate the nutrition queries at write time, so values are fresh by the
  // time we reach this page. This re-invalidation is kept as a safety net for
  // any future input path that feeds the backend calculation but isn't wired up.
  useIonViewWillEnter(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.NUTRITIONAL_REQUIREMENT, "calories"],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.NUTRITIONAL_REQUIREMENT, "macros"],
    });
  });

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/summary.svg"
          title="Your personalised nutrition targets"
          subtitle="Built around your goals, body, and lifestyle"
        >
          <div className="flex w-full max-w-[361px] flex-col gap-4">
            <NutritionSummaryCard variant="calories" onAmend={() => setActiveModal("calories")} />
            <NutritionSummaryCard variant="macros" onAmend={() => setActiveModal("macros")} />
          </div>
          <OnboardingNextFab onClick={navigateToNextStep} />
        </OnboardingStepShell>

        <EditDailyCaloriesModal
          isOpen={activeModal === "calories"}
          onDismiss={() => setActiveModal(null)}
        />
        <EditMacrosModal isOpen={activeModal === "macros"} onDismiss={() => setActiveModal(null)} />
      </IonContent>
    </IonPage>
  );
};

export default OnboardingSummaryPage;
